import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { GlobalSearch } from './GlobalSearch';
import { LanguageSelector } from './LanguageSelector';
import {
  LayoutDashboard,
  Users,
  UserCog,
  Shield,
  FileText,
  Stethoscope,
  Pill,
  ClipboardList,
  MessageSquare,
  MapPin,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Bell,
  Search,
  Menu,
  ScanLine,
  Briefcase,
  Package,
  Tag,
  Globe,
  Building2,
  Lock,
  Info,
  CalendarDays,
  GitBranch,
  CheckSquare,
  Layers,
  MessagesSquare,
  Key,
  Wrench,
  Zap,
  Clock,
  Star,
  Book,
  MailCheck,
  Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eeeed7c0-b14c-41e9-a1b8-b122278f0c69/artifacts/auk1nki7_LOGO-EECASA.png";

// Menu items with translation keys - all menu names use t() function
const getMenuItems = (t) => [
  { 
    name: t('nav:menu.dashboard'), 
    path: '/dashboard', 
    icon: LayoutDashboard,
    permissions: []
  },
  { 
    name: t('nav:menu.orders'), 
    path: '/orders', 
    icon: ClipboardList,
    permissions: ['orders.read']
  },
  { 
    name: t('nav:menu.ocrAi'), 
    path: '/ocr', 
    icon: ScanLine,
    permissions: ['ocr.process']
  },
  { 
    name: t('nav:menu.workflows'), 
    path: '/flows', 
    icon: GitBranch,
    permissions: ['config.read'],
    submenu: [
      { name: t('nav:menu.list'), path: '/flows', icon: GitBranch },
      { name: t('nav:menu.tasks'), path: '/tasks', icon: CheckSquare },
      { name: t('common:tools'), path: '/agent-tools', icon: Wrench },
      { name: t('nav:menu.queues'), path: '/agent-queues', icon: Layers },
      { name: t('common:conversations'), path: '/conversations', icon: MessagesSquare }
    ]
  },
  { 
    name: t('nav:menu.specialists'), 
    path: '/specialists', 
    icon: Stethoscope,
    permissions: ['specialists.read'],
    submenu: [
      { name: t('nav:menu.directory'), path: '/specialists', icon: Stethoscope },
      { name: t('common:types'), path: '/specialist-types', icon: Tag },
      { name: t('common:calendar'), path: '/specialist-calendar', icon: CalendarDays }
    ]
  },
  { 
    name: t('nav:menu.medications'), 
    path: '/medications', 
    icon: Pill,
    permissions: ['medications.read'],
    submenu: [
      { name: t('nav:menu.catalog'), path: '/medications', icon: Pill },
      { name: t('nav:menu.inventory'), path: '/inventory', icon: Package },
      { name: 'Enterprise', path: '/enterprise-inventory', icon: Building2 },
      { name: t('nav:menu.compliance'), path: '/compliance', icon: Shield }
    ]
  },
  { 
    name: t('nav:menu.patients'), 
    path: '/patients', 
    icon: Users,
    permissions: ['patients.read'],
    submenu: [
      { name: t('nav:menu.list'), path: '/patients', icon: Users },
      { name: t('common:medicalHistory'), path: '/clinical', icon: FileText }
    ]
  },
  {
    name: t('nav:menu.reports'),
    path: '/reports',
    icon: BarChart3,
    permissions: ['reports.read'],
    submenu: [
      { name: t('nav:menu.information'), path: '/reports', icon: BarChart3 },
      { name: t('common:geolocation'), path: '/geolocation', icon: MapPin },
      { name: t('common:conversations'), path: '/whatsapp', icon: MessageSquare },
      { name: t('reviews:title'), path: '/reviews', icon: Star }
    ]
  },
  {
    name: t('rules:title'),
    path: '/automation',
    icon: Zap,
    permissions: ['config.read'],
    submenu: [
      { name: t('rules:rules.title'), path: '/automation', icon: Settings },
      { name: t('scheduledTasks:title'), path: '/scheduled-tasks', icon: Clock }
    ]
  },
  {
    name: t('nav:menu.information'),
    path: '/information',
    icon: Info,
    permissions: ['config.read'],
    submenu: [
      { name: t('common:locations'), path: '/locations', icon: Globe },
      { name: t('nav:menu.services'), path: '/services', icon: Briefcase }
    ]
  },
  {
    name: t('nav:menu.security'),
    path: '/security',
    icon: Lock,
    permissions: ['users.read'],
    submenu: [
      { name: t('nav:menu.users'), path: '/users', icon: UserCog },
      { name: t('nav:menu.roles'), path: '/rbac', icon: Shield },
      { name: t('common:apiTokens'), path: '/api-tokens', icon: Key },
      { name: t('nav:menu.audit'), path: '/audit', icon: FileText }
    ]
  },
  { 
    name: t('nav:menu.settings'), 
    path: '/settings', 
    icon: Settings,
    permissions: ['config.read']
  },
  { 
    name: t('nav:menu.documentation'), 
    path: '/documentation', 
    icon: Book,
    permissions: []
  },
];

const MenuItem = ({ item, collapsed, onNavigate, location, openSubmenu, onToggleSubmenu }) => {
  const hasSubmenu = item.submenu && item.submenu.length > 0;
  
  // Check if any submenu item is active
  const isSubmenuActive = hasSubmenu && item.submenu.some(sub => location.pathname === sub.path);
  const isActive = location.pathname === item.path || isSubmenuActive;
  
  // This submenu is open if it matches the openSubmenu state
  const isOpen = openSubmenu === item.path;

  // Auto-open submenu if a child is active (only on mount/navigation)
  React.useEffect(() => {
    if (isSubmenuActive && !isOpen) {
      onToggleSubmenu(item.path);
    }
  }, [location.pathname]);

  if (hasSubmenu) {
    return (
      <div className="space-y-1">
        <button
          onClick={() => onToggleSubmenu(isOpen ? null : item.path)}
          className={`sidebar-nav-item w-full ${isActive ? 'active' : ''} ${collapsed ? 'justify-center' : 'justify-between'}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <item.icon className="h-[18px] w-[18px] flex-shrink-0" style={{ strokeWidth: 1.75 }} />
            {!collapsed && <span className="truncate min-w-0" style={{ fontSize: '14px', fontWeight: 500 }}>{item.name}</span>}
          </div>
          {!collapsed && (
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          )}
        </button>
        
        {/* Submenu */}
        {!collapsed && isOpen && (
          <div className="ml-4 pl-4 border-l border-[var(--line)] space-y-1">
            {item.submenu.map((subItem) => (
              <NavLink
                key={subItem.path}
                to={subItem.path}
                onClick={onNavigate}
                data-testid={`nav-${subItem.path.replace('/', '')}`}
                className={({ isActive }) =>
                  `sidebar-nav-item py-2 ${isActive ? 'active' : ''}`
                }
                style={{ fontSize: '13px' }}
              >
                <subItem.icon className="h-4 w-4 flex-shrink-0" style={{ strokeWidth: 1.75 }} />
                <span>{subItem.name}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      data-testid={`nav-${item.path.replace('/', '')}`}
      onClick={onNavigate}
      className={({ isActive }) =>
        `sidebar-nav-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center' : ''}`
      }
    >
      <item.icon className="h-[18px] w-[18px] flex-shrink-0" style={{ strokeWidth: 1.75 }} />
      {!collapsed && <span className="truncate min-w-0" style={{ fontSize: '14px', fontWeight: 500 }}>{item.name}</span>}
    </NavLink>
  );
};

export const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null); // Track which submenu is open (accordion)
  const { user, logout, hasAnyPermission, isAdmin } = useAuth();
  const { t } = useTranslation(['nav', 'common']);
  const location = useLocation();
  const navigate = useNavigate();

  // Get menu items with translations
  const menuItems = getMenuItems(t);

  const filteredMenuItems = menuItems.filter(item => 
    item.permissions.length === 0 || hasAnyPermission(item.permissions) || isAdmin()
  );

  // Find current page including submenus
  const findCurrentPage = () => {
    for (const item of menuItems) {
      if (location.pathname === item.path) return item;
      if (item.submenu) {
        const subItem = item.submenu.find(sub => location.pathname === sub.path);
        if (subItem) return { ...subItem, parent: item.name };
      }
    }
    return null;
  };
  
  const currentPage = findCurrentPage();
  
  // Search modal state
  const [searchOpen, setSearchOpen] = useState(false);

  // Manual email check -- lets an admin trigger IMAP polling on demand
  // instead of relying only on the automatic 30s loop, which is exactly
  // what got the mailbox's login flagged/blocked by Google in the past.
  // Running it by hand, only when actually needed, avoids repeating that.
  const [checkingEmail, setCheckingEmail] = useState(false);

  const handleCheckEmailNow = async () => {
    if (checkingEmail) return;
    setCheckingEmail(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/email-monitor/check-now`);
      const { emails_found = 0, emails_processed = 0 } = response.data || {};
      if (emails_found > 0) {
        toast.success(t('nav:header.checkEmailSuccessFound', { found: emails_found, processed: emails_processed }));
      } else {
        toast.success(t('nav:header.checkEmailSuccessNone'));
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message;
      toast.error(t('nav:header.checkEmailError', { error: detail }));
    } finally {
      setCheckingEmail(false);
    }
  };

  // Keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]" style={{ fontFamily: "'Inter Tight', system-ui, sans-serif" }}>
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full bg-white z-50 transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-64'
        } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        style={{ boxShadow: 'var(--shadow-sm)' }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-center px-4">
          <img src={LOGO_URL} alt="Especialistas En Casa" className={collapsed ? "h-10 w-10 object-contain" : "h-12 object-contain"} />
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {filteredMenuItems.map((item) => (
            <MenuItem
              key={item.path}
              item={item}
              collapsed={collapsed}
              onNavigate={() => setMobileMenuOpen(false)}
              location={location}
              openSubmenu={openSubmenu}
              onToggleSubmenu={setOpenSubmenu}
            />
          ))}
        </nav>

        {/* Collapse Button */}
        <div className="absolute bottom-4 left-0 right-0 px-3 hidden lg:block">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="w-full justify-center text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-soft)]"
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="topbar flex items-center justify-between px-4 lg:px-8">
          {/* Left - Mobile Menu & Page Title */}
          <div className="flex items-center gap-4 min-w-[200px]">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Page Title */}
            <div className="hidden sm:block">
              <h1 className="topbar-title">
                {currentPage?.parent ? `${currentPage.parent} / ${currentPage.name}` : currentPage?.name || 'Dashboard'}
              </h1>
            </div>
          </div>

          {/* Center - Search Button (3x wider) */}
          <div className="flex-1 flex justify-center max-w-3xl mx-4">
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden md:flex items-center gap-2 px-6 h-10 w-full bg-[var(--bg-alt)] rounded-[10px] text-[var(--ink-3)] hover:bg-white hover:shadow-[var(--shadow-sm)] transition-all cursor-pointer"
              data-testid="header-search"
            >
              <Search className="h-4 w-4" />
              <span className="text-[13px] flex-1 text-left">{t('nav:header.search')}</span>
              <kbd className="px-2 py-0.5 text-[10px] font-medium bg-white rounded text-[var(--ink-3)]">⌘K</kbd>
            </button>
          </div>

          {/* Right - Actions */}
          <div className="flex items-center gap-3">
            {/* Mobile Search Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="md:hidden"
              onClick={() => setSearchOpen(true)}
              data-testid="mobile-search-btn"
            >
              <Search className="h-5 w-5 text-[var(--ink-3)]" />
            </Button>

            {/* Language Selector */}
            <LanguageSelector variant="compact" />

            {/* Manual email check -- admin/config.write only, matches the
                backend endpoint's own permission requirement */}
            {(isAdmin() || hasAnyPermission(['config.write'])) && (
              <button
                className="topbar-icon-btn relative"
                data-testid="check-email-now-btn"
                title={t('nav:header.checkEmail')}
                disabled={checkingEmail}
                onClick={handleCheckEmailNow}
              >
                {checkingEmail ? (
                  <Loader2 className="h-[18px] w-[18px] animate-spin" />
                ) : (
                  <MailCheck className="h-[18px] w-[18px]" />
                )}
              </button>
            )}

            {/* Notifications */}
            <button className="topbar-icon-btn relative" data-testid="notifications-btn">
              <Bell className="h-[18px] w-[18px]" />
              <span className="notification-dot"></span>
            </button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="user-pill" data-testid="user-menu-btn">
                  <div className="user-pill-avatar">
                    {getInitials()}
                  </div>
                  <span className="hidden sm:block text-[13px] font-medium text-[var(--ink)]">
                    {user?.first_name} {user?.last_name}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.first_name} {user?.last_name}</span>
                    <span className="text-xs text-[var(--ink-3)] font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => navigate('/profile')}
                  data-testid="menu-profile"
                >
                  <UserCog className="mr-2 h-4 w-4" />
                  {t('nav:header.profile')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/settings')}
                  data-testid="menu-settings"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  {t('nav:header.settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout}
                  className="text-[var(--red)] focus:text-[var(--red)]"
                  data-testid="menu-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('nav:header.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
      
      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

export default Layout;
